'use client';
// VX-3 D5 · R17 — 상세 이미지 블록의 «여는 쪽». 공용 KdGallery(서버 · 다른 레인 소유)를 고치지 않고 감싼다.
//
//   · 갤러리 그림(img[data-kd-shot])을 탭하면 그 컷에서 전체 화면 갤러리가 열린다(클릭 위임 — 공용 마크업 불변).
//   · 히어로 그림 탭은 HeroShotStage 가 window 이벤트(kd:shots-open)로 알려 준다.
//   · 「전체 화면으로 보기」 버튼 = 키보드·읽기 도구의 입구(그림 자체는 버튼이 아니다).
//   · 라이트박스 본체(ShotLightbox)는 «열 때만» 동적 import — 초기 번들에는 이 얇은 껍데기만 든다.
//   · 닫으면 원래 스크롤 위치·포커스로 돌아간다. 관심고객 카드를 누르면 닫고 리드폼 앵커로 간다(리드폼은 옮기지 않는다).
//   · R17 「이미지가 이상해요」 — POST /api/site-image-report { siteId, shot }. 누르면 「접수했습니다」, 같은 컷은 다시 못 누른다
//     (localStorage · 막혀 있으면 이 화면 동안만). 실패는 조용히. 컷이 여러 장이면 블록의 버튼은 전체 화면을 열어 «그 컷» 에서 누르게 한다.
// 계측: gallery_open · gallery_end_lead_click · image_report (lib/analytics · 스키마 변경 0).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { trackFeature } from '@/lib/analytics';
import { hasReported, lightboxSlidesOf, lightboxStartIndex, reportKeyOf, type DetailShot } from '@/lib/apt/detail-vx3';
import { SHOTS_OPEN_EVENT, prefersReducedMotion, readReported, sendImageReport, storeReported, type ShotsOpenDetail } from './shot-events';
import { openLeadSheet } from '@/lib/apt/lead-sheet';

const ShotLightbox = dynamic(() => import('./ShotLightbox'), { ssr: false });

export interface ShotViewerProps {
  shots: DetailShot[];
  siteId: string | null;
  siteSlug?: string | null;
  siteName: string;
  /** 리드폼 앵커 id — 리드폼이 서는 현장에서만 넘긴다(없으면 관심고객 카드도 없다). */
  leadAnchorId?: string | null;
  /** VX-INT — 아래 격자(KdGallery)에 실제로 선 컷 수. 0 이면(1컷 현장 — 그 한 장은 히어로가 썼다) 「전체 화면으로 보기」 를 내지 않는다.
   *  히어로 그림을 눌러 여는 동작·신고 버튼은 그대로. 안 넘기면 종전(항상 낸다). */
  gridCount?: number;
  children?: ReactNode;
}

export default function ShotViewer({ shots, siteId, siteSlug, siteName, leadAnchorId, gridCount, children }: ShotViewerProps) {
  const [open, setOpen] = useState<{ index: number; hint: boolean } | null>(null);
  const [reported, setReported] = useState<readonly string[]>([]);
  const [ready, setReady] = useState(false);
  const restore = useRef<{ y: number; el: HTMLElement | null }>({ y: 0, el: null });
  const reportedRef = useRef<readonly string[]>([]);

  const slides = useMemo(() => lightboxSlidesOf(shots, !!leadAnchorId), [shots, leadAnchorId]);

  const openAt = useCallback((shot: string | null | undefined, via: string, hint = false) => {
    if (slides.length === 0) return;
    const index = lightboxStartIndex(slides, shot);
    const active = document.activeElement;
    restore.current = { y: window.scrollY, el: active instanceof HTMLElement && active !== document.body ? active : null };
    setOpen({ index, hint });
    const s = slides[index];
    try { trackFeature('gallery_open', { site_slug: siteSlug ?? null, shot: s.kind === 'shot' ? s.shot.shot : null, via, count: shots.length }); } catch { /* 계측 실패는 화면을 막지 않는다 */ }
  }, [slides, shots.length, siteSlug]);

  useEffect(() => {
    const list = readReported();
    reportedRef.current = list;
    setReported(list);
    setReady(true);
  }, []);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<ShotsOpenDetail>).detail;
      openAt(d?.shot ?? null, d?.via || 'hero');
    };
    window.addEventListener(SHOTS_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SHOTS_OPEN_EVENT, onOpen);
  }, [openAt]);

  const close = useCallback(() => {
    setOpen(null);
    const { y, el } = restore.current;
    window.setTimeout(() => {
      try { window.scrollTo(0, y); } catch { /* 무시 */ }
      try { el?.focus({ preventScroll: true }); } catch { /* 무시 */ }
    }, 0);
  }, []);

  const toLead = useCallback(() => {
    try { trackFeature('gallery_end_lead_click', { site_slug: siteSlug ?? null, count: shots.length }); } catch { /* 계측 실패는 화면을 막지 않는다 */ }
    setOpen(null);
    window.setTimeout(() => {
      if (leadAnchorId && openLeadSheet('gallery_end')) return; // leadform_ink1 — 같은 폼 한 벌을 바텀시트로(받을 폼이 없으면 종전 스크롤)
      const target = leadAnchorId ? document.getElementById(leadAnchorId) : null;
      if (!target) return;
      try { target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' }); } catch { target.scrollIntoView(); }
      const field = target.querySelector<HTMLElement>('input:not([type="hidden"]):not([aria-hidden="true"]), select, textarea');
      try { field?.focus({ preventScroll: true }); } catch { /* 무시 */ }
    }, 30);
  }, [leadAnchorId, shots.length, siteSlug]);

  const report = useCallback((shot: string) => {
    const key = reportKeyOf(siteId, shot);
    if (!key || !siteId || hasReported(reportedRef.current, key)) return;
    const next = storeReported(reportedRef.current, key);
    reportedRef.current = next;
    setReported(next);
    sendImageReport(siteId, shot);
    try { trackFeature('image_report', { site_slug: siteSlug ?? null, shot }); } catch { /* 계측 실패는 화면을 막지 않는다 */ }
  }, [siteId, siteSlug]);

  const onGalleryClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const t = e.target as Element | null;
    const img = t && typeof t.closest === 'function' ? t.closest('img[data-kd-shot]') : null;
    if (!img) return;
    openAt(img.getAttribute('data-kd-shot'), 'gallery');
  };

  if (shots.length === 0) return <>{children}</>;

  const single = shots.length === 1 ? shots[0] : null;
  const singleDone = single ? hasReported(reported, reportKeyOf(siteId, single.shot)) : false;
  // 격자에 그림이 없으면 «볼 것 없이 떠 있는» 열기 링크를 내지 않는다. 신고만 남으면(또는 아무것도 없으면) 막대도 그에 맞춘다.
  const showOpen = gridCount === undefined || gridCount > 0;
  const showBar = showOpen || !!siteId;

  return (
    <div className={showOpen ? 'kd-shv' : 'kd-shv kd-shv--solo'} data-vx="shot-viewer" data-ready={ready ? '' : undefined}>
      <div className="kd-shv__g" onClick={onGalleryClick}>{children}</div>
      {showBar ? (
      <div className="kd-shv__bar">
        {showOpen ? (
          <button type="button" className="kd-shv__open" onClick={() => openAt(shots[0].shot, 'button')}>
            전체 화면으로 보기{shots.length > 1 ? ` (${shots.length}장)` : ''}
          </button>
        ) : null}
        {siteId ? (
          <button
            type="button"
            className="kd-shv__report"
            data-rd="label"
            disabled={singleDone}
            onClick={() => (single ? report(single.shot) : openAt(shots[0].shot, 'report', true))}
          >
            {singleDone ? '접수했습니다' : '이미지가 이상해요'}
          </button>
        ) : null}
      </div>
      ) : null}
      {open ? (
        <ShotLightbox
          slides={slides}
          startIndex={open.index}
          siteName={siteName}
          siteId={siteId}
          reported={reported}
          reportHint={open.hint}
          onReport={report}
          onLead={toLead}
          onClose={close}
        />
      ) : null}
    </div>
  );
}
